// src/components/profile/AboutSection.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AboutSection.css";
import MemberNumberDrawer from "../popups/MemberNumberDrawer";

const BASE_LANG_OPTIONS = [
  "English","Español","Français","Português","中文","日本語","한국어","Deutsch",
  "Italiano","Indonesia","Tiếng Việt","Русский","हिन्दी","Türkçe","فارسی","العربية",
  "Swahili","Zulu","Xhosa"
];

export default function AboutSection({
  user,
  canVisitStore = false,
  onVisitStore,
  isOwner = false,
  onSaveLanguages,
}) {
  const navigate = useNavigate();

  const initialLangs = Array.isArray(user?.about?.languages)
    ? user.about.languages.slice(0, 5)
    : [];

  const [langs, setLangs] = useState(initialLangs);
  const [draft, setDraft] = useState("");
  const [useOther, setUseOther] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMemberInfo, setShowMemberInfo] = useState(false);

  useEffect(() => {
    const next = Array.isArray(user?.about?.languages)
      ? user.about.languages.slice(0, 5)
      : [];
    setLangs(next);
  }, [user?.about?.languages]);

  const isAdmin = user?.isAdmin === true || user?.adminRole != null;
  const isApp =
    user?.isApp === true ||
    String(user?.accountType || user?.about?.accountType || "primary").toLowerCase() === "app";

  const role = isAdmin
    ? "Admin"
    : isApp
    ? "App"
    : user?.userType === "merchant" && user?.isMerchant
    ? "Merchant"
    : "Pioneer";

  const joined = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : "—";

  const memberNoValue = Number(user?.memberNo);
  const isFirst1000Member =
    Number.isFinite(memberNoValue) &&
    memberNoValue > 0 &&
    memberNoValue <= 1000;

  const memberNoPadded = isFirst1000Member
    ? String(memberNoValue).padStart(3, "0")
    : null;


  const piLinks = Array.isArray(user?.about?.links?._list)
    ? user.about.links._list
    : (user?.about?.links?.pi ? [user.about.links.pi] : []);

  const normalize = (s) => (s || "").trim().replace(/\s+/g, " ");
  const canonical = (s) => {
    if (!s) return s;
    return s
      .split(" ")
      .map((w) => (w[0] ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  };

  const resolvedCountry = (() => {
    if (user?.about?.country) return normalize(String(user.about.country));
    if (typeof user?.location === "string" && user.location.trim().length > 0) {
      return normalize(user.location);
    }
    if (user?.location?.country) return normalize(String(user.location.country));
    if (user?.country) return normalize(String(user.country));
    return "";
  })();

  const country = canonical(resolvedCountry);

  const LANG_OPTIONS = useMemo(() => {
    const filtered = BASE_LANG_OPTIONS.filter((o) => !langs.includes(o));
    return [...filtered, "Other…"];
  }, [langs]);

  const remainingOptions = useMemo(
    () => LANG_OPTIONS.filter((o) => !langs.includes(o)),
    [LANG_OPTIONS, langs]
  );

  const accountType = String(
    user?.accountType || user?.about?.accountType || "primary"
  ).toLowerCase();

  const ownedAppsRaw = Array.isArray(user?.apps)
    ? user.apps
    : Array.isArray(user?.about?.apps)
      ? user.about.apps
      : [];

  const ownedApps = ownedAppsRaw
    .map((app) => {
      if (typeof app === "string") {
        return {
          username: app.trim(),
          name: "",
        };
      }

      return {
        username: String(app?.username || "").trim(),
        name: String(app?.name || "").trim(),
      };
    })
    .filter((app) => app.username);

  const shouldShowApps = accountType !== "app" && ownedApps.length > 0;

  const addDraftLanguage = () => {
    if (langs.length >= 5) return;

    if (useOther) {
      const norm = normalize(otherText);
      if (!norm) return;
      const val = canonical(norm);
      if (langs.some((l) => l.toLowerCase() === val.toLowerCase())) return;
      setLangs((prev) => [...prev, val].slice(0, 5));
      setOtherText("");
      setUseOther(false);
      setDraft("");
      return;
    }

    if (!draft || draft === "Other…") return;
    if (langs.includes(draft)) return;
    setLangs((prev) => [...prev, draft].slice(0, 5));
    setDraft("");
  };

  const onSelectChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    setUseOther(val === "Other…");
  };

  const removeLang = (l) => {
    setLangs((prev) => prev.filter((x) => x !== l));
  };

  const handleSave = async () => {
    if (!onSaveLanguages) return;
    try {
      setSaving(true);
      await onSaveLanguages(langs.slice(0, 5));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="about-card">
        <div className="about-row">
          <div className="about-col"><div className="about-label">Role</div><div className="about-value">—</div></div>
          <div className="about-col"><div className="about-label">Store</div><div className="about-value">—</div></div>
          <div className="about-col"><div className="about-label">Joined</div><div className="about-value">—</div></div>
          <div className="about-col"><div className="about-label">Country</div><div className="about-value">—</div></div>
          <div className="about-col"><div className="about-label">Languages</div><div className="about-value">—</div></div>
          <div className="about-col"><div className="about-label">Links (.pi)</div><div className="about-value">—</div></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="about-card">
        <div className="about-row">
          <div className="about-col">
            <div className="about-label">Role</div>
            <div className="about-value">{role}</div>
          </div>

          <div className="about-col">
            <div className="about-label">Store</div>
            <div className="about-value">
              {user.businessUsername ? user.businessUsername : "—"}
            </div>

            {canVisitStore && user.businessUsername && (
              <div className="about-visitstore">
                <button
                  type="button"
                  className="about-btn"
                  onClick={onVisitStore}
                >
                  Visit Store
                </button>
              </div>
            )}
          </div>

          {shouldShowApps && (
            <div className="about-col">
              <div className="about-label">Apps</div>
              <div className="lang-pills">
                {ownedApps.map((app) => (
                  <button
                    key={app.username}
                    type="button"
                    className="lang-pill lang-pill--readonly about-app-pill"
                    title={app.name || app.username}
                    onClick={() => navigate(`/user/${app.username}`)}
                  >
                    {app.username}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="about-col">
            <div className="about-label">Joined</div>
            <div
              className="about-value"
              style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
            >
              <span>{joined}</span>
              {memberNoPadded && (
                <button
                  type="button"
                  onClick={() => setShowMemberInfo(true)}
                  aria-label="About your Pioneer Number"
                  title="About your Pioneer Number"
                  style={{
                    cursor: "pointer",
                    border: "none",
                    background: "transparent",
                    padding: 0
                  }}
                >
                  <span className="lang-pill lang-pill--readonly">
                    #{memberNoPadded}
                  </span>
                </button>
              )}
            </div>
          </div>

          <div className="about-col">
            <div className="about-label">Country</div>
            <div className="lang-pills">
              {country ? (
                <span className="lang-pill lang-pill--readonly">
                  {country}
                </span>
              ) : (
                <span className="about-value">—</span>
              )}
            </div>
          </div>

          <div className="about-col">
            <div className="about-label">Languages</div>

            <div className="lang-pills">
              {langs.length > 0 ? (
                langs.map((l) => (
                  <span
                    key={l}
                    className={`lang-pill ${!isOwner ? "lang-pill--readonly" : ""}`}
                  >
                    {l}
                    {isOwner && (
                      <button
                        className="lang-pill-x"
                        onClick={() => removeLang(l)}
                        aria-label={`Remove ${l}`}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))
              ) : (
                <span className="about-value">—</span>
              )}
            </div>

            {isOwner && (
              <>
                <div className="lang-add-row">
                  <select
                    value={draft}
                    onChange={onSelectChange}
                    className="lang-select"
                  >
                    <option value="">Select a language…</option>
                    {remainingOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>

                  {useOther && (
                    <input
                      className="lang-input"
                      type="text"
                      placeholder="Type your language…"
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      maxLength={40}
                    />
                  )}

                  <button
                    type="button"
                    className="about-btn"
                    onClick={addDraftLanguage}
                    disabled={
                      langs.length >= 5 ||
                      (!useOther && (!draft || draft === "Other…")) ||
                      (useOther && !normalize(otherText))
                    }
                  >
                    Add
                  </button>
                </div>

                <div className="lang-hint">You can select up to 5.</div>

                <div className="lang-actions">
                  <button
                    type="button"
                    className="about-btn save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save Languages"}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="about-col">
            <div className="about-label">Links (.pi)</div>
            <div className="about-value">
              {piLinks.length > 0 ? piLinks.join(", ") : "—"}
            </div>
          </div>
        </div>
      </div>

      <MemberNumberDrawer
        isOpen={showMemberInfo}
        onClose={() => setShowMemberInfo(false)}
      />
    </>
  );
}
